import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  createQuestionariesFormSchema,
  type QuestionariesFormValues,
} from './validation/questionariesSchema';
import { useQueryClient } from '@tanstack/react-query';
import type { VerificationQuestionariesResponseDto } from '../api/types';

const useQuestionariesForm = () => {
  const { t } = useTranslation();
  const schema = createQuestionariesFormSchema(t);
  const queryClient = useQueryClient();

  const form = useForm<QuestionariesFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: async () => {
      const questionaries = (await queryClient.ensureQueryData({
        queryKey: ['verification-questionaries'],
      })) as VerificationQuestionariesResponseDto;
      return {
        questions: questionaries.questions.map(question => ({
          questionId: question.questionId,
          selectedAnswers: [],
        })),
      };
    },
  });

  const setQuestionAnswer = (questionId: string, answerIds: Array<string>) => {
    const questions = form.getValues('questions');
    const questionIndex = questions.findIndex(q => q.questionId === questionId);

    const newQuestions = questions.map(question =>
      question.questionId === questionId
        ? { ...question, selectedAnswers: answerIds }
        : question
    );

    form.setValue('questions', newQuestions);

    if (answerIds.length > 0 && questionIndex !== -1) {
      form.clearErrors(`questions.${questionIndex}.selectedAnswers`);
    }
  };

  const resetQuestionaries = () => {
    form.reset();
  };

  return {
    form,
    setQuestionAnswer,
    resetQuestionaries,
  };
};

export { useQuestionariesForm };
